import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  PersistentProject,
  organizationsGetOrganizationProjects,
  projectsDeleteProject,
  projectsUpdateProject,
  usersGetUserProjects,
} from "../sdk";

// Assuming UpdateProjectRequest is a type for project update operations
export interface UpdateProjectRequest {
  name?: string;
  description?: string;
  org_id?: string;
  // other project fields that might be updated
}

/**
 * Hook for managing projects with optional organization filtering
 * @param userId - Required user ID
 * @param orgId - Optional organization ID to filter projects
 * @returns Projects management functions and state
 */
export function useProjects(userId: string, orgId?: string) {
  const [projects, setProjects] = useState<PersistentProject[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      let projects: PersistentProject[] = [];
      if (orgId) {
        const response =
          await organizationsGetOrganizationProjects({
            path: { org_id: orgId },
          });
        projects = response.data || [];
      } else {
        const response = await usersGetUserProjects({ path: { user_id: userId } });
        const data = response.data;
        projects = (data && 'results' in data) ? data.results : [];
      }
      setProjects(projects);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
    }
  }, [userId, orgId]);

  const editProject = async (projectId: string, data: UpdateProjectRequest) => {
    try {
      const response = await projectsUpdateProject({
        path: { project_id: projectId },
        body: data,
      });
      const updatedProject = response.data;
      if (updatedProject) {
        setProjects((prev) =>
          prev.map((p) => (p.id === projectId ? updatedProject : p))
        );
      }
    } catch (err) {
      setError(err as Error);
    }
  };

  const deleteProject = async (projectId: string) => {
    try {
      await projectsDeleteProject({ path: { project_id: projectId } });
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      toast.success("Project deleted");
    } catch (err) {
      setError(err as Error);
      toast.error("Failed to delete project");
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return { projects, loading, error, editProject, deleteProject };
}
